
import { useState } from 'react'
import useSWR from 'swr'
import {
  HardDrive, Plus, Trash2, RefreshCw, FolderOpen, CheckCircle2, Globe,
  Database, ArrowRight, Activity,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

const fetcher = (url: string) => fetch(url).then(r => r.json())

interface StorageConnection {
  id: string
  name: string
  bucket_name: string
  endpoint: string | null
  region: string
  is_active: number
  created_at: string
}

interface StorageData {
  connections: StorageConnection[]
}

export default function StoragePage() {
  const { data, mutate } = useSWR<StorageData>('/api/storage', fetcher)
  const [addOpen, setAddOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ name: '', bucket_name: '', endpoint: '', region: 'auto' })

  const connections = data?.connections ?? []

  async function handleAdd() {
    setAdding(true)
    try {
      const res = await fetch('/api/storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) {
        setAddOpen(false)
        setForm({ name: '', bucket_name: '', endpoint: '', region: 'auto' })
        mutate()
      }
    } finally {
      setAdding(false)
    }
  }

  async function handleToggle(id: string, active: boolean) {
    await fetch(`/api/storage/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: active }),
    })
    mutate()
  }

  async function handleDelete(id: string) {
    await fetch(`/api/storage/${id}`, { method: 'DELETE' })
    mutate()
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <HardDrive className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">R2 Storage</h2>
            <p className="text-xs text-muted-foreground">
              {connections.filter(c => c.is_active).length} active bucket{connections.length !== 1 ? 's' : ''} connected
            </p>
          </div>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          size="sm"
          className="gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" /> Connect Bucket
        </Button>
      </div>

      {/* Info callout */}
      <div className="flex items-start gap-2.5 p-3 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
        <Activity className="w-4 h-4 text-cyan-accent flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-foreground">R2 Storage Integration</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Register your Cloudflare R2 buckets to enable <code className="font-mono text-lava">storage:read</code> and{' '}
            <code className="font-mono text-lava">storage:write</code> permissions on API keys. Access via the gateway with
            your <code className="font-mono text-lava">lfk_v1_</code> key.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Buckets', value: connections.length, icon: Database, accent: 'lava' },
          { label: 'Active', value: connections.filter(c => c.is_active).length, icon: CheckCircle2, accent: 'success' },
          { label: 'Regions', value: [...new Set(connections.map(c => c.region))].length || '—', icon: Globe, accent: 'cyan' },
        ].map(stat => {
          const Icon = stat.icon
          const accentMap: Record<string, string> = {
            lava: 'bg-lava/10 text-lava',
            success: 'bg-success/10 text-success',
            cyan: 'bg-cyan-accent/10 text-cyan-accent',
          }
          return (
            <Card key={stat.label} className="bg-card border-border">
              <CardContent className="p-3 flex items-center gap-3">
                <div className={`p-1.5 rounded ${accentMap[stat.accent]}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">{stat.label}</p>
                  <p className="text-lg font-bold text-foreground tabular-nums">{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Connections list */}
      {connections.length === 0 ? (
        <Card className="border-dashed border-border bg-card">
          <CardContent className="py-12 text-center">
            <div className="w-12 h-12 rounded-full bg-lava/10 flex items-center justify-center mx-auto mb-4">
              <HardDrive className="w-5 h-5 text-lava" />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-1">No buckets connected</h3>
            <p className="text-xs text-muted-foreground mb-4 max-w-xs mx-auto">
              Connect your Cloudflare R2 buckets to enable storage operations via API keys
            </p>
            <Button
              onClick={() => setAddOpen(true)}
              size="sm"
              className="lava-gradient text-lava-foreground border-0 gap-2 hover:opacity-90"
            >
              <Plus className="w-3.5 h-3.5" /> Connect First Bucket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {connections.map(conn => (
            <Card
              key={conn.id}
              className={`bg-card border transition-all ${conn.is_active ? 'border-border hover:border-lava/30' : 'border-border/40 opacity-60'}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-md bg-lava/10 flex-shrink-0">
                    <FolderOpen className="w-4 h-4 text-lava" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{conn.name}</h3>
                        <code className="text-[10px] font-mono text-muted-foreground">{conn.bucket_name}</code>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Switch
                          checked={conn.is_active === 1}
                          onCheckedChange={(v) => handleToggle(conn.id, v)}
                          className="scale-75 data-[state=checked]:bg-lava"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete(conn.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline" className="text-[9px] h-4 px-1.5 font-mono border-border text-muted-foreground">
                        {conn.region}
                      </Badge>
                      {conn.endpoint && (
                        <code className="text-[10px] font-mono text-muted-foreground truncate max-w-[200px]">{conn.endpoint}</code>
                      )}
                      <span className={`flex items-center gap-1 text-[10px] ml-auto ${conn.is_active ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${conn.is_active ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
                        {conn.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Connect bucket dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-lava" />
              Connect R2 Bucket
            </DialogTitle>
            <DialogDescription className="text-xs">
              Register a Cloudflare R2 bucket for API key storage access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Display Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 text-xs" placeholder="Assets Bucket" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bucket Name</Label>
              <Input value={form.bucket_name} onChange={e => setForm(f => ({ ...f, bucket_name: e.target.value }))} className="h-8 text-xs font-mono" placeholder="my-r2-bucket" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Custom Endpoint <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={form.endpoint} onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))} className="h-8 text-xs font-mono" placeholder="https://account.r2.cloudflarestorage.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Region</Label>
              <Input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} className="h-8 text-xs font-mono" placeholder="auto" />
            </div>
            <Button
              onClick={handleAdd}
              disabled={adding || !form.name || !form.bucket_name}
              className="w-full h-8 lava-gradient text-lava-foreground border-0 text-xs gap-2 hover:opacity-90"
            >
              {adding ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Connect Bucket
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
